export const EMAIL_ATTACHMENT_MAX_FILES = 3;
export const EMAIL_ATTACHMENT_MAX_TOTAL_BYTES = 15 * 1024 * 1024;
export const EMAIL_ATTACHMENT_TOTAL_SIZE_LIMIT_MB = 15;

const EMAIL_ATTACHMENT_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'rtf',
  'txt',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'tif',
  'tiff',
  'zip',
  'tar',
  'gz',
] as const;

type EmailAttachmentExtension = (typeof EMAIL_ATTACHMENT_EXTENSIONS)[number];

const MIME_TYPES_BY_EXTENSION: Record<
  EmailAttachmentExtension,
  readonly string[]
> = {
  pdf: ['application/pdf'],
  doc: ['application/msword'],
  docx: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/zip',
  ],
  xls: ['application/vnd.ms-excel'],
  xlsx: [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/zip',
  ],
  ppt: ['application/vnd.ms-powerpoint'],
  pptx: [
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/zip',
  ],
  rtf: ['application/rtf', 'text/rtf', 'text/plain'],
  txt: ['text/plain'],
  jpg: ['image/jpeg', 'image/jpg'],
  jpeg: ['image/jpeg', 'image/jpg'],
  png: ['image/png'],
  gif: ['image/gif'],
  bmp: ['image/bmp', 'image/x-ms-bmp'],
  tif: ['image/tiff'],
  tiff: ['image/tiff'],
  zip: ['application/zip', 'application/x-zip-compressed'],
  tar: ['application/x-tar'],
  gz: ['application/gzip', 'application/x-gzip'],
};

export const EMAIL_ATTACHMENT_ACCEPT = EMAIL_ATTACHMENT_EXTENSIONS.map(
  (extension) => `.${extension}`
).join(',');

export const EMAIL_ATTACHMENT_TOOLTIP =
  'Supported email attachments: PDF, DOC/DOCX, XLS/XLSX, PPT/PPTX, RTF/TXT, JPG/PNG/GIF/BMP/TIFF, ZIP/TAR/GZ. Up to 3 files and 15 MB total. Audio and video are not supported.';

const SUPPORTED_TYPE_SUMMARY =
  'Use PDF, Word, Excel, PowerPoint, RTF/TXT, JPG/PNG/GIF/BMP/TIFF, ZIP, TAR, or GZ. Audio and video are not supported.';

interface AttachmentIssue {
  title: string;
  description: string;
}

type AttachmentValidation =
  | { valid: true; files: File[] }
  | ({ valid: false } & AttachmentIssue);

const extensionFor = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot > -1 ? name.slice(dot + 1).toLowerCase() : '';
};

const normalizedMime = (mime: string): string =>
  mime.split(';', 1)[0]?.trim().toLowerCase() ?? '';

const isAllowedExtension = (
  extension: string
): extension is EmailAttachmentExtension =>
  EMAIL_ATTACHMENT_EXTENSIONS.includes(extension as EmailAttachmentExtension);

const duplicateKey = (file: File): string =>
  [
    file.name.trim().toLowerCase(),
    file.size,
    Number.isFinite(file.lastModified) ? file.lastModified : 0,
  ].join(':');

export const validateEmailAttachments = (
  attachments: unknown
): AttachmentValidation => {
  if (!Array.isArray(attachments) || attachments.length === 0) {
    return { valid: true, files: [] };
  }

  if (attachments.length > EMAIL_ATTACHMENT_MAX_FILES) {
    return {
      valid: false,
      title: 'Remove extra attachments',
      description: `You can attach up to ${EMAIL_ATTACHMENT_MAX_FILES} files to an email. Remove ${
        attachments.length - EMAIL_ATTACHMENT_MAX_FILES
      } and try again.`,
    };
  }

  const files: File[] = [];
  const seen = new Set<string>();
  let totalSize = 0;

  for (const attachment of attachments) {
    if (
      typeof File === 'undefined' ||
      !(attachment instanceof File) ||
      !attachment.name.trim()
    ) {
      return {
        valid: false,
        title: 'Remove invalid attachment',
        description:
          'One attachment could not be read. Remove it, attach the original file again, and retry.',
      };
    }

    if (attachment.size === 0) {
      return {
        valid: false,
        title: 'Remove empty attachment',
        description: `"${attachment.name}" is empty. Attach a non-empty file and try again.`,
      };
    }

    if (attachment.size > EMAIL_ATTACHMENT_MAX_TOTAL_BYTES) {
      return {
        valid: false,
        title: 'Choose a smaller attachment',
        description: `"${attachment.name}" is larger than 15 MB. Compress it or attach a smaller file.`,
      };
    }

    const extension = extensionFor(attachment.name);
    if (!isAllowedExtension(extension)) {
      return {
        valid: false,
        title: 'Remove unsupported attachment',
        description: `"${attachment.name}" is not a supported email attachment. ${SUPPORTED_TYPE_SUMMARY}`,
      };
    }

    // A browser may omit a MIME type or use the generic octet-stream value.
    // In those cases the server still validates the file signature. For every
    // specific declaration, require agreement with the filename extension:
    // neither client-supplied MIME nor a renamed extension is trusted alone.
    const mime = normalizedMime(attachment.type);
    if (
      mime &&
      mime !== 'application/octet-stream' &&
      !MIME_TYPES_BY_EXTENSION[extension].includes(mime)
    ) {
      return {
        valid: false,
        title: 'Fix the attachment type',
        description: `"${attachment.name}" does not match its reported file type. Export the original file again and retry.`,
      };
    }

    const fingerprint = duplicateKey(attachment);
    if (seen.has(fingerprint)) {
      return {
        valid: false,
        title: 'Remove duplicate attachment',
        description: `"${attachment.name}" is attached more than once. Keep one copy and try again.`,
      };
    }

    seen.add(fingerprint);
    totalSize += attachment.size;
    files.push(attachment);
  }

  if (totalSize > EMAIL_ATTACHMENT_MAX_TOTAL_BYTES) {
    return {
      valid: false,
      title: 'Reduce total attachment size',
      description:
        'Attachments can total up to 15 MB. Remove a file or attach smaller versions and try again.',
    };
  }

  return { valid: true, files };
};

export const addEmailAttachmentPolicy = <
  T extends Record<string, unknown> | null | undefined,
>(
  fields: T[]
): T[] =>
  fields.map((field) => {
    if (!field || field.type !== 'editor') return field;

    return {
      ...field,
      attachmentFiles: EMAIL_ATTACHMENT_ACCEPT,
      attachmentTooltipMessage: EMAIL_ATTACHMENT_TOOLTIP,
      totalSizeLimitMB: EMAIL_ATTACHMENT_TOTAL_SIZE_LIMIT_MB,
    };
  });
